import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrainCircuit, AudioLines, PenTool, Sparkles } from "lucide-react";

const STAGES = [
  { label: "Analyzing your idea...", icon: BrainCircuit, duration: 1800 },
  { label: "Applying your voice...", icon: AudioLines, duration: 2200 },
  { label: "Crafting your post...", icon: PenTool, duration: 2500 },
  { label: "Polishing output...", icon: Sparkles, duration: 3000 },
];

interface GenerationProgressProps {
  isActive: boolean;
}

export function GenerationProgress({ isActive }: GenerationProgressProps) {
  const [stageIndex, setStageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setStageIndex(0);
      setProgress(0);
      return;
    }

    setStageIndex(0);
    setProgress(0);

    let currentStage = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const advanceStage = () => {
      if (currentStage < STAGES.length - 1) {
        currentStage++;
        setStageIndex(currentStage);
      }
    };

    let elapsed = 0;
    for (let i = 0; i < STAGES.length - 1; i++) {
      elapsed += STAGES[i].duration;
      timers.push(setTimeout(advanceStage, elapsed));
    }

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 92) return prev;
        const increment = prev < 30 ? 2.5 : prev < 60 ? 1.5 : prev < 80 ? 0.8 : 0.3;
        return Math.min(prev + increment, 92);
      });
    }, 200);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(progressInterval);
    };
  }, [isActive]);

  if (!isActive) return null;

  const stage = STAGES[stageIndex];
  const StageIcon = stage.icon;

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8" role="status" aria-live="polite" aria-busy="true">
      <div className="relative w-16 h-16">
        <motion.div
          className="absolute inset-0 rounded-2xl bg-primary/10"
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.2, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 rounded-2xl bg-primary/5 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={stageIndex}
              initial={{ opacity: 0, scale: 0.8, y: 5 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StageIcon className="w-7 h-7 text-primary" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="w-full max-w-xs space-y-3">
        <div className="h-2 bg-secondary/50 rounded-full overflow-hidden relative" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} aria-label="Post generation progress">
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
          <motion.div
            className="absolute inset-y-0 left-0 h-full pointer-events-none"
            style={{
              background: "transparent",
              boxShadow: "8px 0 20px 4px hsl(205 88% 52% / 0.65)",
            }}
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.p
            key={stageIndex}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="text-sm font-medium text-muted-foreground text-center"
          >
            {stage.label}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
